import { type RequestCookies } from '@shared/request_type'
import { type User } from '@/features/account/services/User/types'
import { getIsZipOnlySignedInUser } from '@/features/account/services/User/utils'
import { guestStoreCookie } from '@/features/account/services/GuestUser/cookies'
import { userStoreCookie } from '@/features/shop/services/ShoppingStore/cookies'
import { SEO_PAGE_TYPE, type SEOHeadProps } from '@/features/shared/components/SEOHeader/types'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { getCanonicalUrl } from '@/features/shared/utils/seo/seoConfig'
import { getRetailerShopBreadcrumbs } from '@/features/shop/utils/seo'

/**
 * the deals page SEO, shared by the pages router DealsPage (through SEOHead) and
 * the app router CDUI deals page (through buildPageSeo + JsonLd) so the two
 * experiment arms can't drift apart. always canonical to the public /shop URL,
 * never the internal /shop-next one.
 */
export const getDealsPageSeo = (storeSlug: string | undefined, storeName: string) => {
  const pathname = `/shop/${storeSlug}/deals`
  const canonicalUrl = getCanonicalUrl(pathname)
  const title = `${storeName} Deals, Sales & Discounts | Shipt`
  const description = `Discover the latest deals at ${storeName}. Browse price drops and featured offers available near you and start saving today. Shop today!`

  const breadcrumbs: SEOHeadProps['breadcrumbs'] = getRetailerShopBreadcrumbs(
    storeSlug,
    storeName,
    [{ name: title, item: canonicalUrl }],
  )

  const pageTypeSchema: SEOHeadProps['pageTypeSchema'] = {
    '@context': 'https://schema.org',
    '@type': [SEO_PAGE_TYPE.WEB_PAGE, SEO_PAGE_TYPE.COLLECTION_PAGE],
    name: title,
    description,
    url: canonicalUrl,
    image: {
      url: objectStorageUrl('get-started-hero.png'),
      width: 872,
      height: 1072,
    },
  }

  return { pathname, canonicalUrl, title, description, breadcrumbs, pageTypeSchema }
}

/**
 * server counterpart to useUserStoreName. reads the store the request settled on,
 * so call it after inferZipAndStoreForSlugifiedRouteRsc where that resolution writes
 * the guest / zip only store onto the request's cookies object and the updated
 * member onto the query cache, which is where the user should come from.
 */
export const getStoreNameSSR = (cookies: RequestCookies, user: User | undefined): string => {
  const userStore = getIsZipOnlySignedInUser(user) ? userStoreCookie.from(cookies) : user?.store
  return userStore?.name || guestStoreCookie.from(cookies)?.name || ''
}
